import {css, StyleSheet} from "aphrodite";

import {sizing, spacing} from "@khanacademy/wonder-blocks-tokens";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {AppleButton} from "@khan/auth-apple";
import {
    CleverLibraryActiveWrapper,
    CleverLibraryButton,
} from "@khan/auth-clever";
import {FacebookButton} from "@khan/auth-facebook";
import {GoogleButton} from "@khan/auth-google";
import {MicrosoftButton} from "@khan/auth-microsoft";
import {authType, type Role} from "@khan/auth-shared";

interface Props {
    /**
     * If supplied, the class code to associate with the user after logging in.
     */
    classCode?: string;
    /**
     * The desired URL to redirect to after logging in. Note: this could be
     * overridden by calculateNextUrl
     */
    continueUrl?: string;
    /**
     * The role of the user to set after sign up.
     */
    role?: Role;
    /**
     * Function to set the server error message.
     */
    setServerError: (message: string) => void;
    /**
     * The size of the buttons.
     */
    size?: "large" | "medium" | "small";
    /**
     * Whether to only show the icon and not the text. The list will display
     * horizontally.
     */
    iconOnly?: boolean;
}

/**
 * A list of OAuth buttons for logging in. For Sign up, see
 * birthday-and-signup-subform.tsx
 */
export function OAuthButtons(props: Props) {
    const {
        classCode,
        role,
        setServerError,
        continueUrl,
        iconOnly,
        size = "large",
    } = props;

    const commonButtonProps = {
        size,
        role,
        continueUrl,
        classCodes: classCode ? [classCode] : undefined,
        onError: setServerError,
        iconOnly,
    };

    const shouldShowMicrosoft = useFlagOrCookie("microsoft-sso");

    return (
        <ul className={css(iconOnly ? styles.buttonListIconOnly : null)}>
            <ListItem iconOnly={false} style={styles.googleButtonWrapper}>
                <GoogleButton
                    {...commonButtonProps}
                    iconOnly={false}
                    minorConversions={[{id: "login_google_button_clicked"}]}
                    context={authType.LOGIN}
                />
            </ListItem>
            <CleverLibraryActiveWrapper>
                <ListItem iconOnly={iconOnly}>
                    <CleverLibraryButton
                        size={size}
                        isLogin={true}
                        continueUrl={continueUrl}
                        iconOnly={iconOnly}
                    />
                </ListItem>
            </CleverLibraryActiveWrapper>
            <ListItem iconOnly={iconOnly}>
                <FacebookButton
                    {...commonButtonProps}
                    minorConversions={[{id: "login_facebook_button_clicked"}]}
                    context={authType.LOGIN}
                />
            </ListItem>
            <ListItem iconOnly={iconOnly}>
                <AppleButton
                    {...commonButtonProps}
                    minorConversions={[{id: "login_apple_button_clicked"}]}
                    context={authType.LOGIN}
                />
            </ListItem>
            {shouldShowMicrosoft && (
                <ListItem iconOnly={iconOnly}>
                    <MicrosoftButton
                        {...commonButtonProps}
                        // minorConversions={[{id: "login_microsoft_button_clicked"}]}
                    />
                </ListItem>
            )}
        </ul>
    );
}

/**
 * Styled component for the oauth button list.
 */
function ListItem({
    iconOnly,
    children,
    style,
}: {
    iconOnly?: boolean;
    children: React.ReactNode;
    style?: React.CSSProperties;
}): React.ReactNode {
    return (
        <li
            className={css(
                styles.signupButtonWrapper,
                styles.listItem,
                iconOnly ? styles.listItemIconOnly : null,
                style ? style : null,
            )}
        >
            {children}
        </li>
    );
}

const styles = StyleSheet.create({
    signupButtonWrapper: {
        marginBlockStart: 4,
    },
    buttonListIconOnly: {
        display: "flex",
        flexWrap: "wrap",
        columnGap: sizing.size_160,
        rowGap: sizing.size_080,
    },
    listItem: {
        marginBlockEnd: spacing.xxxSmall_4,
    },
    listItemIconOnly: {
        margin: 0,
        flex: "1 0 auto",
    },
    googleButtonWrapper: {
        width: "100%",
    },
});
